import { DataIngestionServiceAPI } from '@API/data-services/data-ingestion-service'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { RootState } from '@AppState/redux-store'
import { LoadingRows } from '@Components/util/loading-rows'
import { Badge, Flex, Section, Skeleton, Table, Text } from '@radix-ui/themes'
import { useQuery } from '@tanstack/react-query'
import { Fragment, useCallback, useState } from 'react'
import { CollapsibleTags } from '@Components/imports/collapsible-tags'
import { useSelector } from 'react-redux'
import { Link, useSearchParams } from 'react-router-dom'
import {
  formatDateToMonthDayShortYearString,
  formatDateToTimeZoneTime,
} from '@Components/util/utils'
import { formatDistanceToNow, formatDuration, intervalToDuration } from 'date-fns'
import './styles.scss'
import { UploadIcon } from '@radix-ui/react-icons'
import { parseImportMessage } from './utils'
import { DataImportFileType, DataImportStatus } from '@Generated/back-office-client/src'
import { DateFilterSection } from './date-filter-section'
import { SearchFiltersSection } from './search-filters-section'
import { DateFilterMode, ImportHistoryQueryParams, TypeaheadOption } from './types'
import { Routes } from '@Components/main-app/types'

const parseUrlParam = (searchParams: URLSearchParams, paramName: ImportHistoryQueryParams) => {
  const params = searchParams.get(paramName)
  if (params?.includes(',')) {
    return params.split(',')
  }
  return params
}

export const ImportHistory = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const [searchParams, setSearchParams] = useSearchParams()

  const [selectedFileType, setSelectedFileType] = useState<DataImportFileType[] | undefined>(
    parseUrlParam(searchParams, ImportHistoryQueryParams.FILE_TYPE) as DataImportFileType[],
  )
  const [selectedStatus, setSelectedStatus] = useState<DataImportStatus | undefined>(
    parseUrlParam(searchParams, ImportHistoryQueryParams.STATUS) as DataImportStatus,
  )
  const [selectedTag, setSelectedTag] = useState<string[]>(
    parseUrlParam(searchParams, ImportHistoryQueryParams.TAG) as string[],
  )

  const [dateMode, setDateMode] = useState<DateFilterMode>(
    (parseUrlParam(searchParams, ImportHistoryQueryParams.DATE_MODE) as DateFilterMode) ||
      DateFilterMode.RECENT,
  )
  const [startDate, setStartDate] = useState<string>(
    searchParams.get(ImportHistoryQueryParams.START_DATE) || '',
  )
  const [startTime, setStartTime] = useState<string>(
    searchParams.get(ImportHistoryQueryParams.START_TIME) || '',
  )

  const updateSearchParams = useCallback(
    (key: ImportHistoryQueryParams, value: string | string[] | null | undefined) => {
      const newParams = new URLSearchParams(searchParams)
      if (value instanceof Array && value.length) {
        newParams.set(key, value.join(','))
      } else if (!(value instanceof Array) && value) {
        newParams.set(key, value)
      } else {
        newParams.delete(key)
      }
      setSearchParams(newParams)
    },
    [searchParams, setSearchParams],
  )

  const handleFileTypeChange = useCallback(
    (change: readonly TypeaheadOption[]) => {
      const values = change.map((c) => c.value as DataImportFileType)
      setSelectedFileType(values)
      updateSearchParams(ImportHistoryQueryParams.FILE_TYPE, values)
    },
    [updateSearchParams],
  )

  const handleStatusChange = useCallback(
    (change: TypeaheadOption | null) => {
      const status = change ? (change.value as DataImportStatus) : undefined
      setSelectedStatus(status)
      updateSearchParams(ImportHistoryQueryParams.STATUS, status)
    },
    [updateSearchParams],
  )

  const handleTagChange = useCallback(
    (change: readonly TypeaheadOption[]) => {
      const values = change.map((c) => c.value as DataImportFileType)
      setSelectedTag(values)
      updateSearchParams(ImportHistoryQueryParams.TAG, values)
    },
    [updateSearchParams],
  )

  const handleDateModeChange = useCallback(
    (mode: DateFilterMode) => {
      setDateMode(mode)
      if (mode === DateFilterMode.RECENT) {
        setStartDate('')
        setStartTime('')
        const newParams = new URLSearchParams(searchParams)
        newParams.set(ImportHistoryQueryParams.DATE_MODE, mode)
        newParams.delete(ImportHistoryQueryParams.START_DATE)
        newParams.delete(ImportHistoryQueryParams.START_TIME)
        setSearchParams(newParams)
      } else {
        updateSearchParams(ImportHistoryQueryParams.DATE_MODE, mode)
      }
    },
    [searchParams, setSearchParams, updateSearchParams],
  )

  const handleStartDateChange = useCallback(
    (date: string) => {
      setStartDate(date)
      updateSearchParams(ImportHistoryQueryParams.START_DATE, date || null)
    },
    [updateSearchParams],
  )

  const handleStartTimeChange = useCallback(
    (time: string) => {
      setStartTime(time)
      updateSearchParams(ImportHistoryQueryParams.START_TIME, time || null)
    },
    [updateSearchParams],
  )

  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone

  const { data: importTags, isLoading: tagsLoading } = useQuery({
    queryKey: [`${activeCustomerId}-import-tags`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return DataIngestionServiceAPI.getImportTags()
    },
  })

  const { data: importHistory, isLoading: historyLoading } = useQuery({
    queryKey: [
      `${activeCustomerId}-import-history`,
      selectedFileType,
      selectedStatus,
      selectedTag,
      dateMode,
      startDate,
      startTime,
    ],
    refetchInterval: 15000,
    queryFn: async () => {
      let startDateTime: Date | undefined
      if (dateMode === DateFilterMode.STARTING_FROM && startDate) {
        const dateTimeString = startTime ? `${startDate}T${startTime}` : startDate
        startDateTime = new Date(dateTimeString)
      }

      return DataIngestionServiceAPI.searchImports({
        fileTypes: selectedFileType?.length ? selectedFileType : undefined,
        status: selectedStatus ?? undefined,
        tags: selectedTag?.length ? selectedTag : undefined,
        startDate: startDateTime,
      })
    },
  })

  const mapImportHistory = useCallback(() => {
    if (!importHistory) {
      return []
    }

    return importHistory.map((history) => {
      const showCollapsibleTags = history.tags.length > 5

      const importResults = parseImportMessage(history.message)

      const filename = history.fileName.split('/')

      const duration = history.finishedAt
        ? formatDuration(
            intervalToDuration({
              start: history.startedAt,
              end: history.finishedAt,
            }),
            { format: ['hours', 'minutes', 'seconds'] },
          )
        : '-'

      return (
        <Fragment key={history.id}>
          <Table.Row align="center">
            <Table.RowHeaderCell className="cell-no-border cell-border-right bottom-text">
              <Flex gap="5">
                <UploadIcon className="upload-icon" />
                <Flex direction="column" gap="1">
                  <Link
                    className="import-id-link"
                    to={Routes.IMPORT_DETAILS.replace(':importId', history.id)}
                    state={{ dataImport: history, returnSearch: searchParams.toString() }}>
                    {history.id}
                  </Link>
                  <Text size="1">{filename[filename.length - 1]}</Text>
                </Flex>
              </Flex>
            </Table.RowHeaderCell>
            <Table.Cell className="cell-border-right">{history.fileType}</Table.Cell>
            <Table.Cell className="cell-border-right">
              {history.finishedAt ? (
                <Flex direction="column">
                  <Flex gap="2">
                    <Text>{formatDateToMonthDayShortYearString(history.finishedAt)}</Text>
                    <Text>-</Text>
                    <Text>{formatDateToTimeZoneTime(history.finishedAt, localTimezone)}</Text>
                  </Flex>
                  <Text size="1">
                    {formatDistanceToNow(history.finishedAt, { addSuffix: true })}
                  </Text>
                </Flex>
              ) : null}
            </Table.Cell>
            <Table.Cell className="cell-border-right">{history.status}</Table.Cell>
            <Table.Cell className="cell-border-right">
              <Flex
                gap="2"
                wrap="wrap"
                align="center"
                title={`Total: ${importResults.totalRows}, New: ${importResults.newRows}, Updated: ${importResults.updatedRows}, Unchanged: ${importResults.unchangedRows}, Error: ${importResults.errorRows}`}>
                <Flex direction="column">
                  <Text align="center">{importResults.totalRows}</Text>
                  <Text>{importResults.totalRows > 1 ? 'rows' : 'row'}</Text>
                </Flex>
                <Badge
                  size="3"
                  color="green"
                  variant={importResults.newRows > 0 ? 'soft' : 'outline'}>
                  {importResults.newRows}
                </Badge>
                <Badge
                  size="3"
                  color="blue"
                  variant={importResults.updatedRows > 0 ? 'soft' : 'outline'}>
                  {importResults.updatedRows}
                </Badge>
                <Badge
                  size="3"
                  color="gray"
                  variant={importResults.unchangedRows > 0 ? 'soft' : 'outline'}>
                  {importResults.unchangedRows}
                </Badge>
                <Badge
                  size="3"
                  color="red"
                  variant={importResults.errorRows > 0 ? 'soft' : 'outline'}>
                  {importResults.errorRows}
                </Badge>
              </Flex>
            </Table.Cell>
            <Table.Cell>{duration ? duration : history.finishedAt ? '< 1 second' : ''}</Table.Cell>
          </Table.Row>
          <Table.Row>
            <Table.Cell className="cell-border-right" />
            <Table.Cell colSpan={5}>
              {showCollapsibleTags ? (
                <CollapsibleTags tags={history.tags} variant="pills" />
              ) : (
                <Flex gap="3" wrap="wrap">
                  {history.tags.map((t) => (
                    <Badge key={t} variant="outline" radius="full">
                      {t}
                    </Badge>
                  ))}
                </Flex>
              )}
            </Table.Cell>
          </Table.Row>
        </Fragment>
      )
    })
  }, [importHistory, localTimezone, searchParams])

  const loading = historyLoading || tagsLoading

  return (
    <>
      <div className="sticky-filters-container">
        <DateFilterSection
          dateMode={dateMode}
          startDate={startDate}
          startTime={startTime}
          onDateModeChange={handleDateModeChange}
          onStartDateChange={handleStartDateChange}
          onStartTimeChange={handleStartTimeChange}
        />

        <SearchFiltersSection
          selectedFileTypes={selectedFileType}
          selectedStatus={selectedStatus}
          selectedTags={selectedTag}
          importTags={importTags}
          onFileTypeChange={handleFileTypeChange}
          onStatusChange={handleStatusChange}
          onTagChange={handleTagChange}
        />
      </div>

      <Section className="section-width" size="1">
        <Skeleton loading={loading}>
          <Table.Root variant="surface">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Import ID</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Import Type</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Finished At</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Result</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Duration</Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>

            <Table.Body>{loading ? <LoadingRows /> : mapImportHistory()}</Table.Body>
          </Table.Root>
        </Skeleton>
      </Section>
    </>
  )
}
